import React, { useCallback } from "react";
import ArrowRight from "../../../image/icons/map/details/ArrowRight.svg?react";
import HeLemanBadge from "../../../image/icons/map/transportBadges/HeLemanBadge.svg";
import BicycleIcon from "../../../image/icons/map/transportIcons/BicycleIcon.svg";
import BlaBlaCarIcon from "../../../image/icons/map/transportIcons/BlaBlaCarIcon.svg";
import BlaBlaDailyIcon from "../../../image/icons/map/transportIcons/BlaBlaDailyIcon.svg";
import BusIcon from "../../../image/icons/map/transportIcons/BusIcon.svg";
import CarIcon from "../../../image/icons/map/transportIcons/CarIcon.svg";
import CargoBicycleIcon from "../../../image/icons/map/transportIcons/CargoBicycleIcon.svg";
import MopedIcon from "../../../image/icons/map/transportIcons/MopedIcon.svg";
import ScooterSeatedIcon from "../../../image/icons/map/transportIcons/ScooterSeatedIcon.svg";
import ScooterStandingIcon from "../../../image/icons/map/transportIcons/ScooterStandingIcon.svg";
import SharingBikeIcon from "../../../image/icons/map/transportIcons/SharingBikeIcon3.svg";
import TrainIcon from "../../../image/icons/map/transportIcons/TrainIcon.svg";
import TramIcon from "../../../image/icons/map/transportIcons/TramIcon.svg";
import WalkIcon from "../../../image/icons/map/transportIcons/WalkingIcon.svg?react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { formatTime } from "../../../tools/TimeRelated";
import { getItineraryLegColorHex } from "../../../tools/TransportModeColorsTools";
import {
    getRentalVehicleDisplayGroup,
    getTransportModeGroupForItineraryLeg,
    RentalVehicleDisplayGroup,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

interface ItineraryDetailsSummaryProps {
    "legs":        ItineraryLegDTO[] | undefined;
    "onLegClick":  ( leg: ItineraryLegDTO ) => void;
    "selectedLeg": ItineraryLegDTO | null;
    "isDarkUi":    boolean;

    /** Compact layout for the Dynamic Island slot; default is panel spacing. */
    "layout"?: "panel" | "island";

    /**
     * Optional island-only wrapper for each leg chip (Motion injected by Dynamic Island).
     * Called only when `layout="island"`.
     */
    "wrapLegItem"?: (
        node: React.ReactNode,
        context: {
            "leg":        ItineraryLegDTO;
            "index":      number;
            "isSelected": boolean;
        },
    ) => React.ReactNode;
}

interface ItineraryDetailsSummaryLegIconProps {
    "leg":         ItineraryLegDTO;
    "style":       React.CSSProperties;
    "primaryText": string;
    "isCompact":   boolean;
}

interface ItineraryDetailsSummaryLegButtonProps {
    "isSelected":  boolean;
    "isCompact":   boolean;
    "leg":         ItineraryLegDTO;
    "onLegClick":  ( leg: ItineraryLegDTO ) => void;
    "primaryText": string;
}

interface MonochromeTransportIconProps {
    "iconSrc":    string;
    "alt":        string;
    "color":      string;
    "className"?: string;
}

const MonochromeTransportIcon: React.FC<MonochromeTransportIconProps> = ( {
    iconSrc,
    alt,
    color,
    className = "",
} ) => (
    <span
        aria-label={alt}
        className={`inline-block shrink-0 ${ className }`}
        role="img"
        style={{
            "backgroundColor": color,
            "WebkitMask":      `url(${ iconSrc }) center / contain no-repeat`,
            "mask":            `url(${ iconSrc }) center / contain no-repeat`,
        }}
    />
);

const ItineraryDetailsSummaryLegIcon: React.FC<ItineraryDetailsSummaryLegIconProps> = ( {
    leg,
    style,
    primaryText,
    isCompact,
} ) => {
    const routeShortName = leg.route?.shortName;
    const rentalDisplayGroup = leg.rental?.vehicle?.type != null
        ? getRentalVehicleDisplayGroup( leg.rental.vehicle.type )
        : RentalVehicleDisplayGroup.BICYCLE;

    const modeColor = getItineraryLegColorHex( leg );
    const transportIconClass = ( useWideMargin: boolean ): string => {
        if ( isCompact ) {
            return "mr-1 h-5 w-5";
        }

        return useWideMargin ? "mr-2 h-7 w-7" : "mr-1 h-7 w-7";
    };
    const durationClass = isCompact ? "text-[9px] font-bold" : "text-[10px] font-bold";
    const routeBadgeClass = isCompact
        ? "m-0 flex items-center justify-center rounded px-1 py-0 text-[9px] font-bold text-white"
        : "m-0 flex items-center justify-center rounded-md p-1 text-[10px] font-bold text-white";
    const walkIconClass = isCompact
        ? "mr-1 inline-block h-4 w-4 shrink-0 align-middle"
        : "mr-1 inline-block w-2/2 shrink-0 align-middle";

    // Render a compact visual per leg mode for the horizontal summary bar.
    switch ( getTransportModeGroupForItineraryLeg( leg ) ) {
        case TransportModeGroup.CAR:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="Car"
                        className={transportIconClass( true )}
                        color={modeColor}
                        iconSrc={CarIcon}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        case TransportModeGroup.MY_CAR:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="Car"
                        className={transportIconClass( true )}
                        color={modeColor}
                        iconSrc={CarIcon}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        case TransportModeGroup.TRAIN:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="Train"
                        className={transportIconClass( false )}
                        color={modeColor}
                        iconSrc={TrainIcon}
                    />
                    <p
                        className={routeBadgeClass}
                        style={{ "backgroundColor": getItineraryLegColorHex( leg ) }}
                    >
                        {routeShortName}
                    </p>
                </div>
            );

        case TransportModeGroup.TRAM:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="Tram"
                        className={transportIconClass( false )}
                        color={modeColor}
                        iconSrc={TramIcon}
                    />
                    <p
                        className={routeBadgeClass}
                        style={{ "backgroundColor": getItineraryLegColorHex( leg ) }}
                    >
                        {routeShortName}
                    </p>
                </div>
            );

        case TransportModeGroup.BUS:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="Bus"
                        className={transportIconClass( false )}
                        color={modeColor}
                        iconSrc={BusIcon}
                    />
                    <p
                        className={routeBadgeClass}
                        style={{ "backgroundColor": getItineraryLegColorHex( leg ) }}
                    >
                        {routeShortName}
                    </p>
                </div>
            );

        case TransportModeGroup.WALK:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <WalkIcon
                        aria-label="Walk"
                        className={walkIconClass}
                        style={{ "color": modeColor }}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        case TransportModeGroup.BICYCLE:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="Bicycle"
                        className={transportIconClass( false )}
                        color={modeColor}
                        iconSrc={BicycleIcon}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        case TransportModeGroup.RENTAL:
        {
            let rentalIconSrc = SharingBikeIcon;
            let rentalAlt = "Vélo";

            switch ( rentalDisplayGroup ) {
                case RentalVehicleDisplayGroup.CARGO_BICYCLE:
                    rentalIconSrc = CargoBicycleIcon;
                    rentalAlt = "Vélo cargo";
                    break;

                case RentalVehicleDisplayGroup.SCOOTER_STANDING:
                    rentalIconSrc = ScooterStandingIcon;
                    rentalAlt = "Troninette";
                    break;

                case RentalVehicleDisplayGroup.SCOOTER_SEATED:
                    rentalIconSrc = ScooterSeatedIcon;
                    rentalAlt = "Trotinette assise";
                    break;

                case RentalVehicleDisplayGroup.CAR:
                    rentalIconSrc = CarIcon;
                    rentalAlt = "Voiture";
                    break;

                case RentalVehicleDisplayGroup.MOPED:
                    rentalIconSrc = MopedIcon;
                    rentalAlt = "Moto / Scooter";
                    break;

                default:
                    break;
            }

            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt={rentalAlt}
                        className={transportIconClass( true )}
                        color={modeColor}
                        iconSrc={rentalIconSrc}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );
        }

        case TransportModeGroup.BLABLADAILY:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="BlaBlaDaily"
                        className={transportIconClass( true )}
                        color={modeColor}
                        iconSrc={BlaBlaDailyIcon}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        case TransportModeGroup.BLABLACAR:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <MonochromeTransportIcon
                        alt="BlablaCar"
                        className={transportIconClass( true )}
                        color={modeColor}
                        iconSrc={BlaBlaCarIcon}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        case TransportModeGroup.HE_LEMAN:
            return (
                <div
                    className="flex items-center"
                    style={style}
                >
                    <img
                        alt="HeLeman"
                        className={transportIconClass( true )}
                        src={HeLemanBadge}
                    />
                    <p
                        className={durationClass}
                        style={{ "color": primaryText }}
                    >
                        {formatTime( leg.duration, true )}
                    </p>
                </div>
            );

        default:
            return null;
    }
};

const ItineraryDetailsSummaryLegButton: React.FC<ItineraryDetailsSummaryLegButtonProps> = ( {
    isSelected,
    isCompact,
    leg,
    onLegClick,
    primaryText,
} ) => {
    const handleClick = useCallback( () => {
        onLegClick( leg );
    }, [ leg, onLegClick ] );

    return (
        <button
            aria-label="Zoom on leg"
            aria-pressed={isSelected}
            className={`flex items-center justify-center cursor-pointer rounded-xl border-2 bg-transparent transition-transform duration-200 ease-out ${
                isCompact ? "p-1" : "p-2"
            } ${
                isSelected
                    ? isCompact
                        ? "scale-[1.01] shadow-sm bg-ionic-primary/10 ring-1 ring-ionic-primary/35"
                        : "scale-105 shadow-md bg-ionic-primary/10 ring-2 ring-ionic-primary/35"
                    : "border-transparent"
            }`}
            onClick={handleClick}
            type="button"
        >
            <ItineraryDetailsSummaryLegIcon
                isCompact={isCompact}
                leg={leg}
                primaryText={primaryText}
                style={{ "color": getItineraryLegColorHex( leg ) }}
            />
        </button>
    );
};

const ItineraryDetailsSummary: React.FC<ItineraryDetailsSummaryProps> = ( {
    legs,
    onLegClick,
    selectedLeg,
    isDarkUi,
    layout = "panel",
    wrapLegItem,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const safeLegs = legs ?? [];
    const isIslandLayout = layout === "island";

    const legRow = (
        <div className={isIslandLayout ? "flex h-full w-fit m-auto items-center px-0.5" : "m-1 flex items-center"}>
            {/* Keep entries non-shrinking so long itineraries stay horizontally scrollable. */}
            {safeLegs.map( ( leg, index ) => {
                const isSelected = selectedLeg !== null && selectedLeg.id === leg.id;

                return (
                    <div
                        className="flex items-center"
                        key={`${ leg.mode }-${ index }`}
                        style={{ "flex": "0 0 auto" }}
                    >
                        {( () => {
                            const legButton = (
                                <ItineraryDetailsSummaryLegButton
                                    isCompact={isIslandLayout}
                                    isSelected={isSelected}
                                    leg={leg}
                                    onLegClick={onLegClick}
                                    primaryText={palette.overlayTextPrimary}
                                />
                            );

                            if ( isIslandLayout && wrapLegItem !== undefined ) {
                                return wrapLegItem( legButton, {
 leg,
index,
isSelected,
} );
                            }

                            return legButton;
                        } )()}
                        {index < safeLegs.length - 1
                            ? (
                                <ArrowRight
                                    className={
                                        isIslandLayout
                                            ? "ml-1 mr-1 inline-block h-[11px] w-[6px] shrink-0 self-center align-middle"
                                            : "ml-2 mr-2 inline-block h-[14px] w-[7px] shrink-0 self-center align-middle"
                                    }
                                    style={{ "color": palette.overlayTextSecondary }}
                                />
                            )
                            : null}
                    </div>
                );
            } )}
        </div>
    );

    if ( isIslandLayout ) {
        return (
            <div
                className="h-full"
                id="ItineraryDetailsSummary"
            >
                {legRow}
            </div>
        );
    }

    return (
        <div
            className="overflow-x-auto overflow-y-hidden"
            id="ItineraryDetailsSummary"
        >
            {legRow}
        </div>
    );
};

export default ItineraryDetailsSummary;
